import type { Routes } from '@angular/router';

import { authGuard } from './core/guards/auth.guard';
import { attendeeOnlyGuard } from './core/sdk/attendee-only.guard';

/**
 * MOB-100 Stage 18 — top-level route table for `apps/mobile`.
 *
 * Stage 17 reserved `/modal/*` for the auth modal stack; Stage 18 fills
 * it in under `/auth/*` (the modal-style overlays are non-modal screens
 * — pushing a route is more aligned with Ionic stacked-page UX).
 *
 * The cold-launch entry-point (`/`) routes through `RootRedirectPage`,
 * which decides between landing and Your Events based on the hydrated
 * token bag. The decision happens synchronously after `AuthService.
 * initialize()` resolves (APP_INITIALIZER chain in `app.config.ts`).
 *
 * `loadComponent` / `loadChildren` lazy boundaries are non-negotiable —
 * see `.cursor/skills/virtrio-angular/SKILL.md`.
 */
export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('./features/root-redirect/root-redirect.page').then((m) => m.RootRedirectPage)
  },
  {
    path: 'auth',
    loadChildren: () =>
      import('./features/auth/auth.routes').then((m) => m.AUTH_ROUTES)
  },
  {
    path: 'your-events',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/your-events/your-events.page').then((m) => m.YourEventsPage)
  },
  {
    path: 'tabs',
    canActivate: [authGuard],
    loadChildren: () =>
      import('./features/tabs/tabs.routes').then((m) => m.TABS_ROUTES)
  },
  {
    // MOB-100 Stage 26 — full-screen QR scanner overlay. Authenticated
    // only; Stage 22 (my QR) and Stage 25 (booth-rep) both push onto
    // this route from their tab contexts.
    path: 'scanner',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/qr-scanner/qr-scanner.page').then((m) => m.QrScannerPage)
  },
  {
    // MOB-100 Stage 21 — Mobile Session Room (BRD §4.5).
    // Pinned 16:9 player + draggable bottom sheet with Chat/Q&A/Polls/
    // People/Transcript. Pre-event / live / recorded states driven by
    // the BFF-authoritative `phase` enum.
    path: 'session/:id',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/session-room/session-room.page').then((m) => m.SessionRoomPage)
  },
  {
    // MOB-100 — native assessment (quiz) taker. Reached from the in-app
    // broadcast `OpenQuiz` action; full-screen so the quiz owns the
    // viewport without the tab bar competing for space.
    path: 'assessment/:id',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/assessment/assessment.page').then((m) => m.AssessmentPage)
  },
  {
    // MOB-100 Stage 25 — booth-rep dashboards (BRD §4.4 + §4.11.2).
    // Top-level so the scanner / OCR / chat surfaces own the full
    // viewport without the tab bar competing for space.
    //
    // F-SDK REQ-F-SDK-074 — `attendeeOnlyGuard` makes these routes
    // unreachable when a partner host has asked for an attendee-only
    // experience. The section entry point on the Me tab is hidden by the
    // same flag; the guard covers direct navigation and notification taps.
    path: 'booth-rep',
    canActivate: [authGuard, attendeeOnlyGuard],
    loadChildren: () =>
      import('./features/booth-rep/booth-rep.routes').then((m) => m.BOOTH_REP_ROUTES)
  },
  {
    path: '**',
    redirectTo: ''
  }
];
